<script setup lang="ts">
import { Heading, Text } from '~ds/components/ui/typography';

import type { FaqSection } from '~/graphql/generated/graphql';

import { usePublicFaqSections } from '~/composables/Learn/usePublicFaqSections';
import { useSectionFaqList } from '~/composables/Learn/useSectionFaqList';
import { UNIFIED_ROUTE_PRIVACY_POLICY, UNIFIED_ROUTE_TERMS } from '~/constants/routes/unified';
import { FAQ_SECTION_LABEL_KEYS, faqSectionFromRouteParam, faqSectionRouteSegment, RETIRED_FAQ_SECTION_SEGMENT } from '~/utils/faq/faqSectionLabelKeys';

const { t } = useI18n();
definePageMeta({
  authType: [AUTH_ROUTE_TYPE_NONE],
  name: 'unified-faq',
  layout: 'unified-public',
  unavailableOnSchools: { navigateToLink: '/' },
  // validate is the first global middleware, so the retired segment must pass here for the redirect middleware to run.
  validate: route => route.params.section === RETIRED_FAQ_SECTION_SEGMENT || faqSectionFromRouteParam(route.params.section) !== undefined,
});

const route = useRoute();
const section = faqSectionFromRouteParam(route.params.section) as FaqSection;
const sectionSegment = faqSectionRouteSegment(section);
const sectionLabel = computed(() => t(FAQ_SECTION_LABEL_KEYS[section]));

useSeoMeta({
  title: () => t('unified.seo.faqSection.title', { section: sectionLabel.value }),
});

const [publicSections, { faqs, isFetching, error }] = await Promise.all([
  usePublicFaqSections(section),
  useSectionFaqList(section),
]);

if (publicSections.error.value) {
  showError({ statusCode: 500, statusMessage: t('unified.errors.genericFailed') });
}
else if (!publicSections.sections.value.includes(section)) {
  showError({ statusCode: 404, statusMessage: t('unified.errors.pageNotFound') });
}

const isEmpty = computed(() => !isFetching.value && faqs.value.length === 0);

const analytics = useAnalyticsService();
onMounted(() => {
  analytics.track(EVENTS.UNIFIED_PUBLIC_PAGE_VIEWED, { page: 'faq', section: sectionSegment });
});
const handleFaqClick = (faqHeading: string) => {
  analytics.track(EVENTS.LEARN_FAQ_OPENED, {
    page: 'learn-faq',
    section: sectionSegment,
    faqHeading: slugify(faqHeading),
  });
};

watchEffect(() => {
  if (error.value) {
    showError({ statusCode: 500, statusMessage: error.value.message });
  }
});
</script>

<template>
  <div>
    <KUICoreCard
      rounding="4xl"
      elevation="none"
      class="mb-10"
      data-testid="faq-section"
    >
      <KUICoreFaqSection
        :faqs="faqs"
        heading-align="center"
        is-rich-text
        background-color="off-white"
        :is-loading="isFetching"
        @faq-opened="handleFaqClick"
      >
        <template #title>
          <Heading
            size="2xl"
            as="h1"
            class="mb-10"
          >
            {{ sectionLabel }}
          </Heading>
        </template>
      </KUICoreFaqSection>
      <Text
        v-if="isEmpty"
        class="text-center"
        data-testid="faq-empty"
      >
        {{ t('unified.faq.empty') }}
      </Text>
      <div class="flex flex-col gap-4 items-center pt-6 lg:pt-20">
        <KUICoreLink
          :to="UNIFIED_ROUTE_PRIVACY_POLICY"
          :text="t('general.footerLinks.privacyPolicy')"
          size="lg"
          class="underline"
        />
        <KUICoreLink
          :to="UNIFIED_ROUTE_TERMS"
          :text="t('general.footerLinks.termsAndConditions')"
          size="lg"
          class="underline"
        />
      </div>
    </KUICoreCard>
  </div>
</template>
